import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, limit, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import FarmAvatar from "../Common/FarmAvatar";
import {
  getActiveRelevantCommunityAlerts,
  getCommunityAlertExpiryLabel,
  getCommunityAlertStatusLabel,
  getTimestampDate,
  hasUsableCommunityAlertViewerLocation,
  isCommunityAlertPost,
  isEligibleCommunityPost,
  isResolvedCommunityAlert,
} from "../../utils/communityAlerts";

const formatAlertDate = (timestamp) => {
  const date = getTimestampDate(timestamp);
  if (!date) return "Recently";

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getLocationLabel = (alert = {}) =>
  alert.locationText ||
  [alert.city, alert.state].filter(Boolean).join(", ") ||
  alert.county ||
  alert.locationNote ||
  "Location not listed";

const sortNewest = (alerts = []) =>
  [...alerts].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

const CommunityAlertsPage = ({ currentUser, userLocation, userProfile }) => {
  const [activeCandidates, setActiveCandidates] = useState([]);
  const [resolvedCandidates, setResolvedCandidates] = useState([]);
  const [viewMode, setViewMode] = useState("active");

  useEffect(() => {
    const activeQuery = query(
      collection(db, "communityPosts"),
      where("alertStatus", "==", "active"),
      limit(50)
    );
    const resolvedQuery = query(
      collection(db, "communityPosts"),
      where("alertStatus", "==", "resolved"),
      limit(50)
    );

    const unsubActive = onSnapshot(
      activeQuery,
      (snapshot) => {
        setActiveCandidates(
          snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        );
      },
      (error) => {
        console.error("Community active alerts listener failed:", error);
        setActiveCandidates([]);
      }
    );
    const unsubResolved = onSnapshot(
      resolvedQuery,
      (snapshot) => {
        setResolvedCandidates(
          snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        );
      },
      (error) => {
        console.error("Community resolved alerts listener failed:", error);
        setResolvedCandidates([]);
      }
    );

    return () => {
      unsubActive();
      unsubResolved();
    };
  }, []);

  const activeAlerts = useMemo(
    () =>
      getActiveRelevantCommunityAlerts(
        activeCandidates,
        userLocation || userProfile || {}
      ),
    [activeCandidates, userLocation, userProfile]
  );
  const hasViewerLocation = hasUsableCommunityAlertViewerLocation(
    userLocation || userProfile || {}
  );
  const resolvedAlerts = useMemo(
    () =>
      sortNewest(
        resolvedCandidates.filter(
          (alert) =>
            isCommunityAlertPost(alert) &&
            isEligibleCommunityPost(alert) &&
            isResolvedCommunityAlert(alert)
        )
      ),
    [resolvedCandidates]
  );
  const displayedAlerts = viewMode === "active" ? activeAlerts : resolvedAlerts;

  return (
    <main className="community-alerts-page page-shell page-shell-wide">
      <section className="community-alerts-hero">
        <div>
          <p className="eyebrow">Community Alerts</p>
          <h1>Important local updates</h1>
          <p>
            Time-sensitive community posts for nearby road closures, missing livestock,
            scam warnings, cancellations, and urgent farm notices.
          </p>
        </div>
        <Link className="btn-soft" to="/community">
          Back to Community
        </Link>
      </section>

      <div className="community-alert-tabs" role="tablist" aria-label="Community alert status">
        <button
          type="button"
          className={viewMode === "active" ? "active" : ""}
          onClick={() => setViewMode("active")}
        >
          Active ({activeAlerts.length})
        </button>
        <button
          type="button"
          className={viewMode === "resolved" ? "active" : ""}
          onClick={() => setViewMode("resolved")}
        >
          Resolved ({resolvedAlerts.length})
        </button>
      </div>

      {!hasViewerLocation && viewMode === "active" && (
        <div className="community-alert-location-note" role="status">
          Showing active Community Alerts.{" "}
          <Link to="/edit-profile#profile-location-section">
            Set your location
          </Link>{" "}
          to prioritize alerts near you.
        </div>
      )}

      {displayedAlerts.length === 0 ? (
        <section className="community-empty-state">
          <strong>
            {viewMode === "active" ? "No active Community Alerts nearby." : "No resolved alerts yet."}
          </strong>
          <p>
            {viewMode === "active"
              ? "Local alerts will appear here when nearby community members post time-sensitive updates."
              : "Resolved alerts remain available through Community history when they exist."}
          </p>
        </section>
      ) : (
        <section className="community-alert-list">
          {displayedAlerts.map((alert) => {
            const statusLabel = getCommunityAlertStatusLabel(alert);
            const preview = String(alert.text || "").slice(0, 220);
            const distanceLabel =
              typeof alert.distanceMiles === "number"
                ? `${Math.round(alert.distanceMiles)} miles away`
                : "";

            return (
              <article className="community-alert-list-card" key={alert.id}>
                <div className="community-alert-list-topline">
                  <span>{statusLabel}</span>
                  <span>{formatAlertDate(alert.createdAt)}</span>
                </div>
                <h2>{alert.title || "Community Alert"}</h2>
                <p>{preview}{String(alert.text || "").length > 220 ? "..." : ""}</p>
                {alert.imageUrl && (
                  <img src={alert.imageUrl} alt={alert.title || "Community Alert"} />
                )}
                <div className="community-alert-list-meta">
                  <span>{getLocationLabel(alert)}</span>
                  {distanceLabel && <span>{distanceLabel}</span>}
                  <span>{getCommunityAlertExpiryLabel(alert)}</span>
                </div>
                <div className="community-alert-list-author">
                  <FarmAvatar
                    profile={{
                      id: alert.authorId,
                      uid: alert.authorId,
                      name: alert.authorName || "Community member",
                      photo: alert.authorPhoto,
                      farmAnimal: alert.authorFarmAnimal,
                      farmAnimalSelectionExplicit:
                        alert.authorFarmAnimalSelectionExplicit === true,
                    }}
                    className="community-comment-avatar-inner"
                  />
                  <span>{alert.authorName || "Community member"}</span>
                </div>
                <Link className="btn-primary" to={`/community?post=${encodeURIComponent(alert.id)}`}>
                  Open post
                </Link>
              </article>
            );
          })}
        </section>
      )}
    </main>
  );
};

export default CommunityAlertsPage;
